import { useCallback, useMemo, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { pluralizeWithCount } from "@common/helpers/string";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import {
    useApplyScheduleTemplateBulkMutation,
    useScheduleTemplates,
} from "@api/scheduleTemplate.api";
import { invalidateScheduleReview } from "@api/scheduleReview.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { InjectedCommandOption } from "@components/commands/lib";
import {
    BulkScheduleRow,
    partitionBulkScheduleRows,
} from "@components/accounting/schedules/bulk/bulkScheduleRows";
import { useAccrualAccountTypeLookup } from "@components/accounting/schedules/useAccrualAccountType";
import { toTransactionBulkScheduleRow } from "@components/accounting/schedules/bulk/transactionBulkScheduleRow";
import { UseTransactionsQueryParam } from "@components/transactions/useTransactionsQuery";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { loadTransactions } from "@components/transactions/useTransactionsQuery";
import { SCHEDULE_REVIEW_TOAST_KEY } from "./scheduleReview.constants";
import { drainFilteredTransactions } from "./transactionsFilterDrain";

interface UseSuspiciousConfirmAllParams {
    entityId: number | null;
    filters: UseTransactionsQueryParam["filters"];
}

function withoutReplacement(options: {
    row: TransactionListItem;
    accountType: AccountType | null;
}): BulkScheduleRow {
    return {
        ...toTransactionBulkScheduleRow({
            transaction: options.row,
            accountType: options.accountType,
        }),
        existingApplicationIds: [],
    };
}

/**
 * "Confirm all / filtered" for the suspicious transactions task: applies the
 * picked template to every transaction currently matching the task filters,
 * page by page. Applied rows leave the suspicious filter, so the drain
 * restarts from the first page after each page with progress; rows that were
 * skipped or failed stay in the filter and are settled once, not re-submitted.
 */
export function useSuspiciousConfirmAll({
    entityId,
    filters,
}: UseSuspiciousConfirmAllParams) {
    const workspace = useActiveWorkspace();
    const workspaceId = workspace?.id ?? "";
    const { data: templates } = useScheduleTemplates();
    const getAccrualAccountType = useAccrualAccountTypeLookup();
    const applyBulk = useApplyScheduleTemplateBulkMutation();
    const { toast, error } = useToaster();
    const [isDraining, setIsDraining] = useState(false);

    const eligibleTemplates = useMemo(
        () =>
            entityId == null
                ? []
                : (templates ?? []).filter((template) =>
                      template.entityIds.includes(entityId),
                  ),
        [templates, entityId],
    );

    const confirmAll = useCallback(
        async (template: ScheduleTemplateListItem) => {
            if (entityId == null || isDraining) {
                return;
            }
            setIsDraining(true);
            let appliedCount = 0;
            let skippedCount = 0;
            let firstFailure: string | undefined;
            const settledTransactionIds = new Set<number>();
            try {
                await drainFilteredTransactions<TransactionListItem>({
                    fetchPage: async ({ page, limit }) => {
                        const response = await loadTransactions({
                            filters,
                            page,
                            limit,
                            workspaceId,
                            skipCount: true,
                        });
                        return response.data;
                    },
                    processPage: async (rows) => {
                        const pendingRows = rows.filter(
                            (row) => !settledTransactionIds.has(row.id),
                        );
                        const partition = partitionBulkScheduleRows({
                            rows: pendingRows.map((row) =>
                                withoutReplacement({
                                    row,
                                    accountType: getAccrualAccountType(
                                        row.accrualAccountId,
                                    ),
                                }),
                            ),
                            template,
                        });
                        const sources = partition.items.map(
                            (item) => item.source,
                        );
                        const sourceIds = new Set(
                            sources.flatMap((source) =>
                                source.sourceType ===
                                ScheduleSourceType.transaction
                                    ? [source.transactionId]
                                    : [],
                            ),
                        );
                        for (const row of pendingRows) {
                            if (!sourceIds.has(row.id)) {
                                settledTransactionIds.add(row.id);
                                skippedCount += 1;
                            }
                        }
                        firstFailure ??= partition.skippedReasons[0];
                        if (sources.length === 0) {
                            return { progressed: false };
                        }
                        const result = await applyBulk.mutateAsync({
                            templateId: template.id,
                            payload: { entityId, sources },
                        });
                        for (const failure of result.failed) {
                            if (
                                failure.source.sourceType ===
                                ScheduleSourceType.transaction
                            ) {
                                settledTransactionIds.add(
                                    failure.source.transactionId,
                                );
                            }
                        }
                        appliedCount += result.applied.length;
                        skippedCount += result.failed.length;
                        firstFailure ??= result.failed[0]?.message;
                        return { progressed: result.applied.length > 0 };
                    },
                });
                if (appliedCount === 0) {
                    error(
                        firstFailure
                            ? `${template.name} couldn't be applied: ${firstFailure}`
                            : `${template.name} couldn't be applied`,
                    );
                    return;
                }
                toast(
                    `${template.name} applied to ${pluralizeWithCount("transaction", appliedCount)}` +
                        (skippedCount > 0
                            ? `, ${pluralizeWithCount("transaction", skippedCount)} skipped`
                            : ""),
                    SCHEDULE_REVIEW_TOAST_KEY,
                );
            } catch (drainError) {
                error(
                    drainError instanceof Error
                        ? drainError.message
                        : "Failed to apply schedule",
                );
            } finally {
                invalidateScheduleReview(workspaceId);
                setIsDraining(false);
            }
        },
        [
            applyBulk,
            entityId,
            error,
            filters,
            getAccrualAccountType,
            isDraining,
            toast,
            workspaceId,
        ],
    );

    const options = useMemo<InjectedCommandOption[]>(
        () =>
            eligibleTemplates.map((template) => ({
                id: `schedule-review-confirm-all-${template.id}`,
                label: template.name,
                keywords: [template.name, template.group].filter(Boolean),
                disabled: isDraining,
                onSelect: () => void confirmAll(template),
            })),
        [confirmAll, eligibleTemplates, isDraining],
    );

    return { options, isDraining };
}
